import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Loader2 } from 'lucide-react';

function DuplicateGoalModal({ isOpen, onClose, onComplete, goal }) {
    const [duplicateToOtherPlatform, setDuplicateToOtherPlatform] = useState(false);
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [loadingApps, setLoadingApps] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [error, setError] = useState('');

    const targetPlatform = goal?.platform === 'android' ? 'ios' : 'android';

    useEffect(() => {
        if (duplicateToOtherPlatform && goal) {
            loadTargetApps();
        }
    }, [duplicateToOtherPlatform, goal]);

    const loadTargetApps = async () => {
        setLoadingApps(true);
        setError('');
        try {
            const response = await api.get('/apps', {
                params: { platform: targetPlatform }
            });
            const loadedApps = response.data.rows || [];
            setApps(loadedApps);
            if (loadedApps.length > 0) {
                setSelectedApp(loadedApps[0]);
            } else {
                setSelectedApp(null);
            }
        } catch (err) {
            console.error('Error loading target apps:', err);
            setError('Failed to load apps for target platform');
        } finally {
            setLoadingApps(false);
        }
    };

    if (!isOpen || !goal) return null;

    const handleDuplicate = async () => {
        if (duplicateToOtherPlatform && !selectedApp) {
            setError(`Please select an app for ${targetPlatform.toUpperCase()}`);
            return;
        }

        setDuplicating(true);
        setError('');
        try {
            const payload = {
                duplicateToOtherPlatform,
                destinationAppId: duplicateToOtherPlatform ? selectedApp.id : undefined
            };
            const response = await api.post(`/tests/goals/${goal.id}/duplicate`, payload);
            if (onComplete) {
                onComplete(response.data);
            }
            onClose();
        } catch (err) {
            console.error('Duplication error:', err);
            setError(err.response?.data?.error || 'Failed to duplicate test goal');
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <div className="dialog-overlay" onClick={onClose}>
            <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                <div className="dialog-header">
                    <h3 className="dialog-title">Duplicate Test Goal</h3>
                    <button className="dialog-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="dialog-body" style={{ padding: '20px' }}>
                    {error && (
                        <div className="error-alert" style={{
                            padding: '10px 14px',
                            backgroundColor: '#ef444420',
                            border: '1px solid #ef444460',
                            borderRadius: '6px',
                            color: '#ef4444',
                            fontSize: '13px',
                            marginBottom: '16px'
                        }}>
                            {error}
                        </div>
                    )}

                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                        Are you sure you want to duplicate <strong>"{goal.title || goal.goalText}"</strong>?
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                        <input
                            type="checkbox"
                            id="platform-copy-chk"
                            checked={duplicateToOtherPlatform}
                            onChange={(e) => setDuplicateToOtherPlatform(e.target.checked)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <label htmlFor="platform-copy-chk" style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                            Copy to {targetPlatform.toUpperCase()}
                        </label>
                    </div>

                    {duplicateToOtherPlatform && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                Target {targetPlatform.toUpperCase()} App
                            </label>
                            {loadingApps ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '13px' }}>
                                    <Loader2 size={16} className="animate-spin" />
                                    Loading apps...
                                </div>
                            ) : apps.length === 0 ? (
                                <div style={{ color: '#f59e0b', fontSize: '13px' }}>
                                    ⚠️ No apps found for {targetPlatform.toUpperCase()}. Please upload an app for {targetPlatform.toUpperCase()} first.
                                </div>
                            ) : (
                                <select
                                    value={selectedApp?.id || ''}
                                    onChange={(e) => setSelectedApp(apps.find(a => a.id === e.target.value))}
                                    style={{
                                        width: '100%',
                                        height: '42px',
                                        padding: '0 12px',
                                        borderRadius: '6px',
                                        border: '1px solid var(--border)',
                                        backgroundColor: 'var(--bg-input, #1a2236)',
                                        color: 'var(--text-primary)',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                >
                                    {apps.map(app => (
                                        <option key={app.id} value={app.id}>
                                            {app.displayName || app.name} {app.version ? `(v${app.version})` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </div>

                <div className="dialog-footer">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={duplicating}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={handleDuplicate}
                        disabled={duplicating || (duplicateToOtherPlatform && apps.length === 0)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        {duplicating && <Loader2 size={14} className="animate-spin" />}
                        {duplicating ? 'Duplicating...' : 'Duplicate'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DuplicateGoalModal;
